import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AdminService } from 'src/app/services/admin/admin.service';

interface HsrCourseDate {
  occurrenceId: number | string;
  courseId: number;
  type: 'initial' | 'refresher';
  courseLabel: string;
  startDate: string; // yyyy-MM-dd, for stable chronological sorting
  endDate: string;
  day: string;
  date: string;
  fee: number;
  location: string;
}

@Component({
  selector: 'app-x-hsr-courses',
  templateUrl: './x-hsr-courses.component.html',
  styleUrl: './x-hsr-courses.component.scss'
})
export class XHsrCoursesComponent implements OnInit {
  allUOCCourses: any[] = [];
  courses: any[] = [];
  RefresherCoursePrice = 0;
  InitialOccupationalPrice = 0;
  hsrCourseDates: HsrCourseDate[] = [];
  loadingHsrDates = false;
  hsrDateError = '';

  //store form state code
  selectedShortCourseStateCode: string = '';
  constructor(
    private adminService: AdminService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {

    // Read route query parameters
    this.route.queryParamMap.subscribe(params => {
      this.selectedShortCourseStateCode = (params.get('state_code') ?? 'mel').toUpperCase();
    });


    this.loadHSRCourseData('all_courses');
  }

  loadHSRCourseData(type: string): void {
    this.allUOCCourses = [];
    this.courses = [];
    this.hsrCourseDates = [];
    this.RefresherCoursePrice = 0;
    this.InitialOccupationalPrice = 0;
    this.hsrDateError = '';
    this.loadingHsrDates = true;

    this.adminService
      .GetXCourseListByType('GET_HSR_COURSES_LIST', type, '', '', true, this.selectedShortCourseStateCode)
      .subscribe({
        next: (response: any[]) => {
          this.allUOCCourses = Array.isArray(response) ? response : [];
          this.courses = this.allUOCCourses;

          const initialCourse = this.allUOCCourses.find(
  course => (course.courseCode || '').trim().toUpperCase().includes('(HSR - IA)')
);

const refresherCourse = this.allUOCCourses.find(
  course => (course.courseCode || '').trim().toUpperCase().includes('(HSR - RE)')
);
 
          this.InitialOccupationalPrice = Number(initialCourse?.totalAmount ?? 0);
          this.RefresherCoursePrice = Number(refresherCourse?.totalAmount ?? 0);

          const requests = [
            ...(initialCourse?.id != null
              ? [{ course: initialCourse, type: 'initial' as const }]
              : []),
            ...(refresherCourse?.id != null
              ? [{ course: refresherCourse, type: 'refresher' as const }]
              : [])
          ].map(({ course, type }) =>
            this.adminService.GetXCourseFullDetails(course.id).pipe(
              map((response: any) => ({
                course,
                type,
                detail: Array.isArray(response) ? response[0] : response,
                failed: false
              })),
              catchError(error => {
                console.error(`Could not load ${type} course details:`, error);
                return of({ course, type, detail: null, failed: true });
              })
            )
          );

          if (!requests.length) {
            this.loadingHsrDates = false;
            this.hsrDateError = 'No HSR courses were found.';
            return;
          }

          // Wait for both course details, then merge and sort all occurrences.
          forkJoin(requests).subscribe({
            next: results => {
              this.hsrCourseDates = results
                .flatMap(result => {
                  const occurrences = result.detail?.occurances;
                  if (!Array.isArray(occurrences)) return [];

                  return occurrences
                    .filter((occ: any) => !!occ.startDate)
                    .map((occ: any): HsrCourseDate => {
                      const startDate = String(occ.startDate).slice(0, 10);
                      const endDate = String(occ.endDate || occ.startDate).slice(0, 10);
                      const date = this.parseLocalDate(startDate);
                      const end = this.parseLocalDate(endDate);
                      const dateFormat = new Intl.DateTimeFormat('en-AU', {
                        day: 'numeric', month: 'long', year: 'numeric'
                      });

                      return {
                        occurrenceId: occ.vettrackOccuranceId ?? occ.id,
                        courseId: result.course.id,
                        type: result.type,
                        courseLabel: result.type === 'initial'
                          ? 'Initial Occupational'
                          : 'Refresher Course',
                        startDate,
                        endDate,
                        day: new Intl.DateTimeFormat('en-AU', { weekday: 'long' }).format(date),
                        date: dateFormat.format(date) +
                          (endDate !== startDate ? ` – ${dateFormat.format(end)}` : ''),
                        fee: Number(result.course.totalAmount ?? 0),
                        location: occ.locationName || occ.location || ''
                      };
                    });
                })
                .sort((a, b) =>
                  a.startDate.localeCompare(b.startDate) ||
                  a.type.localeCompare(b.type)
                );

              if (results.some(x => x.failed)) {
                this.hsrDateError = 'Some course dates could not be loaded.';
              }
              this.loadingHsrDates = false;
            },
            error: error => {
              console.error('HSR details error:', error);
              this.loadingHsrDates = false;
              this.hsrDateError = 'Could not load course dates.';
            }
          });
        },
        error: error => {
          console.error('HSR list error:', error);
          this.loadingHsrDates = false;
          this.hsrDateError = 'Could not load HSR courses.';
        }
      });
  }

  // Keep the original separate Refresher Course card beneath the date table.
  // Display its next upcoming date; all dates remain in the merged, sorted table.
  get nextRefresherDate(): HsrCourseDate | null {
    const today = new Date();
    const todayIso = [
      today.getFullYear(),
      String(today.getMonth() + 1).padStart(2, '0'),
      String(today.getDate()).padStart(2, '0')
    ].join('-');

    return this.hsrCourseDates.find(
      item => item.type === 'refresher' && item.startDate >= todayIso
    ) ?? null;
  }

  // Interpret ISO date-only values as local calendar dates (avoids timezone day shifts).
  private parseLocalDate(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  trackHsrDate(index: number, item: HsrCourseDate): string {
    return `${item.courseId}-${item.occurrenceId}-${item.startDate}`;
  }
}
